import type { HTMLAttributes, ReactNode } from 'react';
import styles from './List.module.css';

const cx = (...parts: Array<string | false | undefined | null>) => parts.filter(Boolean).join(' ');

/** White card with hidden overflow holding rows separated by hairlines. */
export function ListContainer({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx(styles.container, className)} {...rest}>
      {children}
    </div>
  );
}

export interface ListHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
}

export function ListHeader({ title, description, meta, actions }: ListHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.headerText}>
        <h3 className={styles.title}>{title}</h3>
        {description && <p className={styles.description}>{description}</p>}
      </div>
      {meta && <div className={styles.meta}>{meta}</div>}
      {actions}
    </div>
  );
}

export interface ListRowProps extends HTMLAttributes<HTMLDivElement> {
  dense?: boolean;
  center?: boolean;
  clickable?: boolean;
}

export function ListRow({ dense, center, clickable, className, children, ...rest }: ListRowProps) {
  return (
    <div
      className={cx(styles.row, dense && styles.dense, center && styles.center, clickable && styles.clickable, className)}
      {...(clickable ? { role: 'link', tabIndex: 0 } : {})}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface CellProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'md' | 'lg' | 'xl';
  right?: boolean;
}

/** Primary cell: `flex: 1 1 260–320px`, `min-width: 0`. */
export function PrimaryCell({ size = 'md', className, children, ...rest }: CellProps) {
  return (
    <div className={cx(styles.primary, size === 'lg' && styles.primaryLg, className)} {...rest}>
      {children}
    </div>
  );
}

/** Secondary cell: `flex: 0 1 130–230px`, `min-width: 0`. */
export function SecondaryCell({ size = 'md', right, className, children, ...rest }: CellProps) {
  return (
    <div
      className={cx(styles.secondary, size === 'lg' && styles.secondaryLg, size === 'xl' && styles.secondaryXl, right && styles.right, className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function RowTitle({ small, children }: { small?: boolean; children: ReactNode }) {
  return <div className={cx(styles.rowTitle, small && styles.rowTitleSm)}>{children}</div>;
}
export function RowMeta({ children }: { children: ReactNode }) {
  return <div className={styles.rowMeta}>{children}</div>;
}
export function RowText({ children }: { children: ReactNode }) {
  return <div className={styles.rowText}>{children}</div>;
}
export function RowFigure({ small, children }: { small?: boolean; children: ReactNode }) {
  return <div className={small ? styles.rowFigureSm : styles.rowFigure}>{children}</div>;
}

/** Every list renders this when empty. Never a placeholder row. */
export function EmptyState({ children }: { children: ReactNode }) {
  return <div className={styles.empty}>{children}</div>;
}
